import { message } from "@/utils/message";
import { useUserStoreHook } from "@/store/modules/user";
import { getFullToken } from "@/utils/auth";

/**
 * Token过期处理工具类
 * 提供统一的token过期检测、处理和用户提示机制
 */
class TokenExpiredHandler {
  private static instance: TokenExpiredHandler;
  private isHandlingExpired = false;

  static getInstance(): TokenExpiredHandler {
    if (!TokenExpiredHandler.instance) {
      TokenExpiredHandler.instance = new TokenExpiredHandler();
    }
    return TokenExpiredHandler.instance;
  }

  /**
   * 检查token是否过期
   * @returns {boolean} true表示已过期，false表示未过期
   */
  isTokenExpired(): boolean {
    const fullToken = getFullToken();
    if (!fullToken || !fullToken.accessToken) {
      return true;
    }

    const now = new Date().getTime();
    const expired = parseInt(String(fullToken.expires)) - now <= 0;
    return expired;
  }

  /**
   * 检查token是否即将过期（5分钟内）
   * @returns {boolean} true表示即将过期，false表示还有足够时间
   */
  isTokenExpiringSoon(): boolean {
    const fullToken = getFullToken();
    if (!fullToken || !fullToken.accessToken) {
      return false;
    }

    const now = new Date().getTime();
    const expiresAt = parseInt(String(fullToken.expires));
    const fiveMinutes = 5 * 60 * 1000; // 5分钟
    const willExpireSoon =
      expiresAt - now <= fiveMinutes && expiresAt - now > 0;

    return willExpireSoon;
  }

  /**
   * 处理token过期
   * @param reason 过期原因
   * @param showMessage 是否显示提示消息
   */
  handleTokenExpired(
    reason: string = "登录已过期",
    showMessage: boolean = true
  ): void {
    if (this.isHandlingExpired) {
      return; // 防止重复处理
    }

    this.isHandlingExpired = true;

    try {
      if (showMessage) {
        message(`${reason}，请重新登录`, { type: "warning" });
      }

      // 执行登出
      useUserStoreHook().logOut();
    } catch (error) {
    } finally {
      // 延迟重置标志，避免页面跳转过程中的重复调用
      setTimeout(() => {
        this.isHandlingExpired = false;
      }, 1000);
    }
  }

  /**
   * 处理即将过期的token
   */
  handleTokenExpiringSoon(): void {
    message("登录即将过期，请及时保存您的工作", {
      type: "warning",
      duration: 10000 // 显示10秒
    });
  }

  /**
   * 启动token过期检查定时器
   * 每分钟检查一次token状态
   */
  startTokenExpirationCheck(): void {
    const checkInterval = 60 * 1000; // 1分钟检查一次

    const timer = setInterval(() => {
      try {
        // 🚨 新增：检查是否在登录页面或白名单页面，避免误报
        const currentPath = window.location.pathname;
        const whiteList = ["/login", "/agreement/user", "/agreement/privacy"];
        if (whiteList.includes(currentPath)) {
          return; // 在白名单页面不进行token检查
        }

        // 🚨 新增：检查是否有token，没有token说明用户未登录，不需要检查过期
        const fullToken = getFullToken();
        if (!fullToken || !fullToken.accessToken) {
          return; // 没有token时不进行过期检查
        }

        if (this.isTokenExpired()) {
          this.handleTokenExpired("登录已过期");
          clearInterval(timer);
          return;
        }

        if (this.isTokenExpiringSoon()) {
          this.handleTokenExpiringSoon();
        }
      } catch (error) { }
    }, checkInterval);

    // 页面卸载时清理定时器
    window.addEventListener("beforeunload", () => {
      clearInterval(timer);
    });
  }

  /**
   * 重置处理状态（主要用于测试）
   */
  reset(): void {
    this.isHandlingExpired = false;
  }
}

// 导出单例实例
export const tokenExpiredHandler = TokenExpiredHandler.getInstance();

// 导出类型定义
export type { TokenExpiredHandler };
